import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { useAnnonces } from "../context/AnnoncesContext";
import * as api from "../api";

const categories = [
  { id: "yachting",     label: "Le Yachting" },
  { id: "moteur",       label: "Bateaux à Moteur" },
  { id: "voile",        label: "La Voile" },
  { id: "equipement",   label: "Équipement" },
  { id: "sports",       label: "Sports et Loisirs" },
  { id: "marinas",      label: "Les Marinas" },
  { id: "navires",      label: "Navires Professionnels" },
  { id: "construction", label: "Construction Navale" },
];

// Sous-catégories par catégorie
const sousCategoriesMap = {
  yachting:     [{ id: "yachts-luxe", label: "Yachts de luxe" }, { id: "yacht-toys", label: "Yacht toys" }],
  moteur:       [{ id: "moteur-cabine", label: "Bateaux à moteur avec cabine" }, { id: "moteur-open", label: "Bateaux à moteur open" }, { id: "pneumatiques", label: "Bateaux pneumatiques" }, { id: "offshore-jetski", label: "Offshore / Jet-skis" }, { id: "electrique", label: "Électriques & hybrides" }],
  voile:        [{ id: "monocoque", label: "Voiliers monocoques" }, { id: "multicoque", label: "Voiliers multicoques" }, { id: "voile-legere", label: "Voile légère" }, { id: "equipement-voile", label: "Équipements & vêtements de voile" }],
  equipement:   [{ id: "greement", label: "Gréements, accastillage" }, { id: "electronique", label: "Électronique marine" }, { id: "moteurs", label: "Moteurs hors-bord et in-bord" }, { id: "propulsion", label: "Propulsion, commandes moteur" }, { id: "pont", label: "Équipements de pont, entretien" }, { id: "securite", label: "Sécurité" }, { id: "vetements", label: "Vêtements & équipements de la personne" }, { id: "confort", label: "Confort à bord" }, { id: "eau-electricite", label: "Eau & électricité" }],
  sports:       [{ id: "kayak", label: "Kayaks, avirons, canoës" }, { id: "plongee", label: "Plongée sous-marine" }, { id: "chasse-marine", label: "Chasse sous-marine" }, { id: "peche", label: "Pêche" }, { id: "glisse", label: "Sports de glisse" }, { id: "loisirs", label: "Bases de loisirs" }],
  marinas:      [{ id: "manutention", label: "Manutention des bateaux" }, { id: "amenagement", label: "Aménagement des marinas" }, { id: "flottant", label: "Maisons flottantes, bateaux maisons" }],
  navires:      [{ id: "transport", label: "Transport de marchandises et passagers" }, { id: "services", label: "Navires de services et spéciaux" }, { id: "travail", label: "Bateaux de services et de travail" }, { id: "tourisme", label: "Activités touristiques et de loisirs" }],
  construction: [{ id: "materiaux", label: "Matériaux et peintures" }, { id: "outillage", label: "Machines de production et outillage" }],
};

const wilayas = [
  "Alger", "Oran", "Annaba", "Béjaïa", "Jijel",
  "Skikda", "Tipaza", "Mostaganem", "Chlef", "Aïn Témouchent"
];

const DOC_TYPES = [
  { id: "catalogue",  label: "📚 Catalogue produits" },
  { id: "plan",       label: "📐 Plan technique" },
  { id: "fiche",      label: "📋 Fiche technique" },
  { id: "certificat", label: "🏅 Certificat / Agrément" },
  { id: "document",   label: "📄 Autre document" },
];

function AjouterAnnonce({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { annonces } = useAnnonces();
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const docInputRef   = useRef(null);

  const annonceToEdit = editMode && id ? annonces.find(a => a.id === parseInt(id)) : null;

  const [step, setStep]               = useState(1);
  const [notification, setNotification] = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [submitted, setSubmitted]     = useState(false);

  const [imageFiles, setImageFiles]           = useState([]);
  const [imagePreviews, setImagePreviews]     = useState(annonceToEdit?.images || []);
  const [videoFile, setVideoFile]             = useState(null);
  const [videoPreview, setVideoPreview]       = useState(null);
  const [uploadedImageUrls, setUploadedImageUrls] = useState(annonceToEdit?.images || []);
  const [uploadedVideoUrl, setUploadedVideoUrl]   = useState(null);
  const [docFiles, setDocFiles]               = useState([]);
  const [uploadedDocs, setUploadedDocs]       = useState([]);

  const [formData, setFormData] = useState({
    nom:                  annonceToEdit?.nom || "",
    description:          annonceToEdit?.description || "",
    descriptionDetaillee: annonceToEdit?.descriptionDetaillee || "",
    prix:                 annonceToEdit?.prix || "",
    categorie:            annonceToEdit?.categorie || "",
    sous_categorie:       annonceToEdit?.sous_categorie || "",
    marque:               annonceToEdit?.marque || "",
    localisation:         annonceToEdit?.localisation || "",
    isNew:                annonceToEdit?.isNew ?? true,
    caracteristiques:     annonceToEdit?.caracteristiques?.join("\n") || "",
  });

  const [ficheTechnique, setFicheTechnique] = useState(() => {
    if (annonceToEdit?.ficheTechnique)
      return Object.entries(annonceToEdit.ficheTechnique).map(([key, value]) => ({ key, value }));
    return [{ key: "", value: "" }];
  });

  // Sous-catégories disponibles selon la catégorie choisie
  const sousCats = formData.categorie ? (sousCategoriesMap[formData.categorie] || []) : [];

  useEffect(() => {
    if (!user || (user.role !== "exposant" && user.role !== "admin")) navigate("/connexion");
  }, [user, navigate]);

  const showNotif = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Réinitialiser la sous-catégorie quand la catégorie change
    if (name === "categorie") {
      setFormData(prev => ({ ...prev, categorie: value, sous_categorie: "" }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    }
  };

  // ✅ Limite photos selon le plan
  const maxPhotos = (() => {
    const plan = user?.plan || "gratuit";
    if (plan === "gratuit") return 3;
    return 10; // premium : illimité dans l'UI (le backend gère)
  })();

  const handleImagesSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newFiles = [...imageFiles, ...files].slice(0, maxPhotos);
    if (imageFiles.length >= maxPhotos) {
      showNotif(`Votre plan permet ${maxPhotos === 3 ? "3 photos maximum" : "10 photos maximum"}. Passez à Premium pour plus !`, "error");
      return;
    }
    setImageFiles(newFiles);
    setImagePreviews(newFiles.map(f => URL.createObjectURL(f)));
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const removeImage = (i) => {
    setImageFiles(imageFiles.filter((_, j) => j !== i));
    setImagePreviews(imagePreviews.filter((_, j) => j !== i));
    setUploadedImageUrls(uploadedImageUrls.filter((_, j) => j !== i));
  };

  const removeVideo = () => {
    setVideoFile(null); setVideoPreview(null); setUploadedVideoUrl(null);
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const handleDocsSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const newEntries = files.map(f => ({ file: f, type_doc: "catalogue", nom_custom: f.name.replace(/\.pdf$/i, "") }));
    setDocFiles(prev => [...prev, ...newEntries].slice(0, 10));
    if (docInputRef.current) docInputRef.current.value = "";
  };

  const removeDoc   = (i) => { setDocFiles(docFiles.filter((_, j) => j !== i)); };
  const updateDocType = (i, v) => setDocFiles(docFiles.map((d, j) => j === i ? { ...d, type_doc: v } : d));
  const updateDocNom  = (i, v) => setDocFiles(docFiles.map((d, j) => j === i ? { ...d, nom_custom: v } : d));

  const uploadAllFiles = async () => {
    const filesToUpload = imageFiles.filter((_, i) => !uploadedImageUrls[i]);
    let newImageUrls = [...uploadedImageUrls];
    let newVideoUrl  = uploadedVideoUrl;
    let newDocs      = [...uploadedDocs];

    if (filesToUpload.length > 0 || videoFile || docFiles.length > 0) {
      setUploading(true);
      try {
        if (filesToUpload.length > 0 || videoFile) {
          const fd = new FormData();
          filesToUpload.forEach(f => fd.append("files", f));
          if (videoFile) fd.append("files", videoFile);
          const res = await api.uploadFiles(fd);
          const urls = res.data.urls;
          let idx = 0;
          for (let i = 0; i < imagePreviews.length; i++) {
            if (!uploadedImageUrls[i] && filesToUpload.length > 0) newImageUrls[i] = urls[idx++];
          }
          if (videoFile && idx < urls.length) newVideoUrl = urls[idx];
          setUploadedImageUrls(newImageUrls);
          setUploadedVideoUrl(newVideoUrl);
        }
        if (docFiles.length > 0) {
          const fdDocs = new FormData();
          docFiles.forEach(d => fdDocs.append("docs", d.file));
          const resDocs = await api.uploadDocs(fdDocs);
          newDocs = resDocs.data.files.map((f, i) => ({
            ...f,
            nom: docFiles[i]?.nom_custom || f.nom,
            type_doc: docFiles[i]?.type_doc || "document"
          }));
          setUploadedDocs(newDocs);
        }
        setUploading(false);
      } catch (err) { setUploading(false); throw new Error("Erreur upload"); }
    }
    return { imageUrls: newImageUrls, videoUrl: newVideoUrl, docs: newDocs };
  };

  const addFicheRow    = () => setFicheTechnique(p => [...p, { key: "", value: "" }]);
  const removeFicheRow = (i) => setFicheTechnique(p => p.filter((_, j) => j !== i));
  const updateFicheRow = (i, f, v) => setFicheTechnique(p => p.map((r, j) => j === i ? { ...r, [f]: v } : r));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (imagePreviews.length === 0) { showNotif("Veuillez ajouter au moins une image", "error"); return; }
    try {
      const { imageUrls, videoUrl, docs } = await uploadAllFiles();
      const ficheObj = {};
      ficheTechnique.forEach(r => { if (r.key.trim()) ficheObj[r.key.trim()] = r.value.trim(); });
      const caracteristiquesArray = formData.caracteristiques.split("\n").map(s => s.trim()).filter(Boolean);
      const annonceData = {
        ...formData,
        images:          imageUrls.filter(Boolean),
        videoUrl:        videoUrl || null,
        documents:       docs,
        ficheTechnique:  ficheObj,
        caracteristiques: caracteristiquesArray,
        exposantId:      user.id,
        exposantNom:     `${user.prenom} ${user.nom}`,
        entreprise:      user.entreprise,
      };
      if (editMode && annonceToEdit) {
        await api.updateAnnonce(annonceToEdit.id, annonceData);
        showNotif("Annonce modifiée avec succès !");
        setTimeout(() => navigate("/mes-annonces"), 1800);
      } else {
        await api.createAnnonce(annonceData);
        setSubmitted(true);
      }
    } catch (err) {
      const code = err.response?.data?.code;
      const msg  = err.response?.data?.message || err.message || "Erreur";
      if (code === "LIMITE_FREE_ATTEINTE" || code === "LIMITE_ANNONCES") {
        showNotif("Limite d'annonces atteinte — Passez à un plan supérieur !", "error");
        setTimeout(() => window.location.href = "/abonnement", 2000);
      } else if (code === "ABONNEMENT_EXPIRE") {
        showNotif("Votre abonnement est expiré — Renouvelez votre plan !", "error");
        setTimeout(() => window.location.href = "/abonnement", 2000);
      } else {
        showNotif(msg, "error");
      }
    }
  };

  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);

  const handleAIEnhance = async () => {
    if (!formData.nom) { showNotif("Remplis le titre d'abord", "error"); return; }
    setAiLoading(true);
    try {
      // On envoie la première image uploadée de l'annonce si elle existe
      const firstImageUrl = uploadedImageUrls[0] || null;
      const res = await api.aiEnhanceAnnonce({
        titre: formData.nom,
        description: formData.description,
        categorie: formData.categorie,
        prix: formData.prix,
        imageUrl: firstImageUrl,
      });
      setAiSuggestions(res.data);
    } catch (e) {
      showNotif("Erreur IA : " + (e.response?.data?.detail || e.message), "error");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestions = () => {
    setFormData(prev => ({
      ...prev,
      nom: aiSuggestions.titre || prev.nom,
      description: aiSuggestions.seo_description || prev.description,
      descriptionDetaillee: aiSuggestions.description || prev.descriptionDetaillee,
      categorie: aiSuggestions.categorie || prev.categorie,
      caracteristiques: aiSuggestions.tags ? aiSuggestions.tags.join("\n") : prev.caracteristiques,
    }));
    setAiSuggestions(null);
    showNotif("✨ Tout a été appliqué ! Vérifiez les étapes 1, 2 et 3.");
  };

  const inputStyle = { width: "100%", padding: "11px 14px", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "14px", outline: "none", boxSizing: "border-box", backgroundColor: "white" };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: "600", color: "#374151", marginBottom: "6px" };
  const steps = ["Informations", "Photos & Vidéo", "Description & Documents"];

  if (submitted) {
    return (
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Navbar />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px" }}>
          <div style={{ maxWidth: "500px", textAlign: "center" }}>
            <div style={{ width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "#f0fdfa", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: "36px" }}>✅</div>
            <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#1f2937", marginBottom: "12px" }}>Annonce soumise avec succès !</h1>
            <div style={{ backgroundColor: "#fef3c7", borderRadius: "12px", padding: "20px", border: "1px solid #fcd34d", marginBottom: "24px" }}>
              <p style={{ fontSize: "15px", color: "#92400e", fontWeight: "600", marginBottom: "8px" }}>⏳ En attente de validation par l'administrateur</p>
              <p style={{ fontSize: "14px", color: "#78350f" }}>Votre annonce sera examinée et publiée par l'administrateur MarinaDZ. Vous recevrez une notification par email une fois acceptée.</p>
            </div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <Link to="/mes-annonces" style={{ padding: "12px 24px", backgroundColor: "#0d9488", color: "white", borderRadius: "8px", textDecoration: "none", fontSize: "14px", fontWeight: "600" }}>Voir mes annonces</Link>
              <button onClick={() => { setSubmitted(false); setStep(1); setFormData({ nom: "", description: "", descriptionDetaillee: "", prix: "", categorie: "", sous_categorie: "", marque: "", localisation: "", isNew: true, caracteristiques: "" }); setImageFiles([]); setImagePreviews([]); setDocFiles([]); }}
                style={{ padding: "12px 24px", backgroundColor: "white", color: "#374151", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>
                Soumettre une autre
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Navbar />

      {notification && (
        <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: 9999, backgroundColor: notification.type === "success" ? "#0d9488" : "#dc2626", color: "white", padding: "14px 22px", borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.2)", fontSize: "14px", fontWeight: "600" }}>
          {notification.type === "success" ? "✓" : "✕"} {notification.msg}
        </div>
      )}

      <div style={{ flex: 1, backgroundColor: "#f9fafb", padding: "40px 50px" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>

          <div style={{ marginBottom: "30px" }}>
            <Link to="/mes-annonces" style={{ color: "#6b7280", fontSize: "14px", textDecoration: "none" }}>← Mes annonces</Link>
            <h1 style={{ fontSize: "26px", fontWeight: "700", color: "#1f2937", marginTop: "8px" }}>
              {editMode ? "Modifier l'annonce" : "Soumettre une annonce"}
            </h1>
            {!editMode && (
              <div style={{ marginTop: "12px", padding: "14px 16px", backgroundColor: "#fef3c7", borderRadius: "10px", border: "1px solid #fcd34d", display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <span style={{ fontSize: "20px" }}>ℹ️</span>
                <div>
                  <p style={{ fontSize: "14px", color: "#92400e", fontWeight: "700", marginBottom: "4px" }}>Validation requise par l'administrateur</p>
                  <p style={{ fontSize: "13px", color: "#78350f" }}>Votre annonce sera soumise à l'administrateur MarinaDZ pour vérification avant d'être publiée sur le site.</p>
                </div>
              </div>
            )}
          </div>

          {/* Steps */}
          <div style={{ display: "flex", marginBottom: "30px", backgroundColor: "white", borderRadius: "12px", padding: "6px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
            {steps.map((s, i) => (
              <button key={i} onClick={() => setStep(i + 1)}
                style={{ flex: 1, padding: "10px", border: "none", borderRadius: "8px", cursor: "pointer", backgroundColor: step === i + 1 ? "#0d9488" : "transparent", color: step === i + 1 ? "white" : "#6b7280", fontSize: "13px", fontWeight: step === i + 1 ? "700" : "500" }}>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", borderRadius: "50%", backgroundColor: step === i + 1 ? "rgba(255,255,255,0.3)" : "#f3f4f6", color: step === i + 1 ? "white" : "#9ca3af", fontSize: "11px", fontWeight: "700", marginRight: "8px" }}>{i + 1}</span>
                {s}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "30px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>

              {/* ── STEP 1 ── */}
              {step === 1 && (
                <div>
                  <h2 style={{ fontSize: "17px", fontWeight: "700", color: "#1f2937", marginBottom: "24px", paddingBottom: "14px", borderBottom: "1px solid #f3f4f6" }}>Informations de base</h2>

                  <div style={{ marginBottom: "20px" }}>
                    <label style={labelStyle}>Titre de l'annonce *</label>
                    <input type="text" name="nom" value={formData.nom} onChange={handleChange} required placeholder="Ex: Chalutier 18m00 en PRVT" style={inputStyle} />
                    {/* Bouton IA */}
                    <button type="button" onClick={handleAIEnhance} disabled={aiLoading}
                      style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px", padding: "9px 16px", backgroundColor: aiLoading ? "#9ca3af" : "#7c3aed", color: "white", border: "none", borderRadius: "8px", cursor: aiLoading ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: "600" }}>
                      {aiLoading
                        ? <><div style={{ width: "14px", height: "14px", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> Analyse en cours...</>
                        : <>✨ Améliorer avec l'IA</>
                      }
                    </button>
                    {/* Suggestions IA */}
                    {aiSuggestions && (
                      <div style={{ marginTop: "14px", backgroundColor: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "12px", padding: "18px" }}>
                        <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#7c3aed", marginBottom: "16px" }}>✨ Suggestions de l'IA — Modifier si besoin puis cliquer Appliquer</h4>

                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ fontSize: "12px", color: "#7c3aed", fontWeight: "700", display: "block", marginBottom: "4px" }}>TITRE AMÉLIORÉ</label>
                          <input type="text" value={aiSuggestions.titre || ""} onChange={e => setAiSuggestions(p => ({ ...p, titre: e.target.value }))}
                            style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd6fe", borderRadius: "8px", fontSize: "14px", fontWeight: "600", color: "#1f2937", outline: "none", boxSizing: "border-box", backgroundColor: "white" }} />
                        </div>

                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ fontSize: "12px", color: "#7c3aed", fontWeight: "700", display: "block", marginBottom: "4px" }}>DESCRIPTION COURTE</label>
                          <input type="text" value={aiSuggestions.seo_description || ""} onChange={e => setAiSuggestions(p => ({ ...p, seo_description: e.target.value }))}
                            style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd6fe", borderRadius: "8px", fontSize: "13px", color: "#374151", outline: "none", boxSizing: "border-box", backgroundColor: "white" }} />
                        </div>

                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ fontSize: "12px", color: "#7c3aed", fontWeight: "700", display: "block", marginBottom: "4px" }}>DESCRIPTION DÉTAILLÉE</label>
                          <textarea value={aiSuggestions.description || ""} onChange={e => setAiSuggestions(p => ({ ...p, description: e.target.value }))} rows={4}
                            style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd6fe", borderRadius: "8px", fontSize: "13px", color: "#374151", outline: "none", boxSizing: "border-box", backgroundColor: "white", resize: "vertical", fontFamily: "inherit" }} />
                        </div>

                        {aiSuggestions.tags?.length > 0 && (
                          <div style={{ marginBottom: "14px" }}>
                            <label style={{ fontSize: "12px", color: "#7c3aed", fontWeight: "700", display: "block", marginBottom: "6px" }}>TAGS SUGGÉRÉS</label>
                            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                              {aiSuggestions.tags.map((tag, i) => (
                                <span key={i} style={{ backgroundColor: "#ede9fe", color: "#7c3aed", padding: "3px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" }}>{tag}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div style={{ display: "flex", gap: "10px" }}>
                          <button type="button" onClick={applyAISuggestions}
                            style={{ padding: "9px 20px", backgroundColor: "#7c3aed", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "700" }}>
                            ✅ Appliquer tout
                          </button>
                          <button type="button" onClick={() => setAiSuggestions(null)}
                            style={{ padding: "9px 16px", backgroundColor: "white", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>
                            Ignorer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Catégorie + Sous-catégorie */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                    <div>
                      <label style={labelStyle}>Catégorie *</label>
                      <select name="categorie" value={formData.categorie} onChange={handleChange} required style={inputStyle}>
                        <option value="">Sélectionner...</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>
                        Sous-catégorie
                        {sousCats.length === 0 && <span style={{ fontWeight: "400", color: "#9ca3af", marginLeft: "6px" }}>(choisir une catégorie d'abord)</span>}
                      </label>
                      <select
                        name="sous_categorie"
                        value={formData.sous_categorie}
                        onChange={handleChange}
                        disabled={sousCats.length === 0}
                        style={{ ...inputStyle, color: sousCats.length === 0 ? "#9ca3af" : "#1f2937", cursor: sousCats.length === 0 ? "not-allowed" : "pointer" }}
                      >
                        <option value="">Toutes les sous-catégories</option>
                        {sousCats.map(sc => <option key={sc.id} value={sc.id}>{sc.label}</option>)}
                      </select>
                      {/* Badge de la sous-catégorie sélectionnée */}
                      {formData.sous_categorie && (
                        <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ backgroundColor: "#f0fdfa", color: "#0d9488", border: "1px solid #99f6e4", borderRadius: "20px", padding: "3px 10px", fontSize: "12px", fontWeight: "600" }}>
                            ✓ {sousCats.find(s => s.id === formData.sous_categorie)?.label}
                          </span>
                          <button type="button" onClick={() => setFormData(p => ({ ...p, sous_categorie: "" }))}
                            style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: "13px" }}>✕</button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
                    <div>
                      <label style={labelStyle}>Marque / Chantier *</label>
                      <input type="text" name="marque" value={formData.marque} onChange={handleChange} required placeholder="Ex: ZORAK, RABIE..." style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Prix</label>
                      <input type="text" name="prix" value={formData.prix} onChange={handleChange} placeholder="Ex: Sur demande" style={inputStyle} />
                    </div>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <label style={labelStyle}>Wilaya *</label>
                    <select name="localisation" value={formData.localisation} onChange={handleChange} required style={inputStyle}>
                      <option value="">Sélectionner...</option>
                      {wilayas.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>

                  <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px", color: "#374151" }}>
                    <input type="checkbox" name="isNew" checked={formData.isNew} onChange={handleChange} style={{ accentColor: "#0d9488", width: "16px", height: "16px" }} />
                    Produit / Service neuf
                  </label>
                </div>
              )}

              {/* ── STEP 2 ── */}
              {step === 2 && (
                <div>
                  <h2 style={{ fontSize: "17px", fontWeight: "700", color: "#1f2937", marginBottom: "8px", paddingBottom: "14px", borderBottom: "1px solid #f3f4f6" }}>Photos & Vidéo</h2>
                  <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "24px" }}>
                    Ajoutez jusqu'à {maxPhotos} photo{maxPhotos > 1 ? "s" : ""}.
                    {user?.plan === "gratuit" && <span style={{ color: "#f59e0b", fontWeight: "600" }}> Plan gratuit : 3 photos max. <a href="/abonnement" style={{ color: "#0d9488" }}>Passez à Premium</a> pour 10 photos.</span>}
                  </p>

                  <div style={{ marginBottom: "30px" }}>
                    <label style={{ ...labelStyle, marginBottom: "12px" }}>📷 Images * <span style={{ fontWeight: "400", color: "#9ca3af" }}>({imagePreviews.length}/{maxPhotos})</span></label>
                    <div onClick={() => imageInputRef.current?.click()}
                      style={{ border: "2px dashed #0d9488", borderRadius: "12px", padding: "30px", textAlign: "center", cursor: "pointer", backgroundColor: "#f0fdfa", marginBottom: "16px" }}
                      onDragOver={e => { e.preventDefault(); e.currentTarget.style.backgroundColor = "#e0fdf4"; }}
                      onDragLeave={e => { e.currentTarget.style.backgroundColor = "#f0fdfa"; }}
                      onDrop={e => { e.preventDefault(); e.currentTarget.style.backgroundColor = "#f0fdfa"; const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/")); if (files.length) { const dt = new DataTransfer(); files.forEach(f => dt.items.add(f)); imageInputRef.current.files = dt.files; handleImagesSelect({ target: imageInputRef.current }); } }}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="1.5" style={{ margin: "0 auto 12px", display: "block" }}>
                        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <p style={{ fontSize: "15px", fontWeight: "600", color: "#0d9488", marginBottom: "4px" }}>Cliquez ou glissez vos images ici</p>
                      <p style={{ fontSize: "12px", color: "#6b7280" }}>JPG, PNG, WEBP — Max 50 Mo</p>
                    </div>
                    <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleImagesSelect} style={{ display: "none" }} />
                    {imagePreviews.length > 0 && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
                        {imagePreviews.map((src, i) => (
                          <div key={i} style={{ position: "relative", borderRadius: "8px", overflow: "hidden", border: i === 0 ? "3px solid #0d9488" : "2px solid #e5e7eb" }}>
                            <img src={src} alt="" style={{ width: "100%", height: "90px", objectFit: "cover", display: "block" }} />
                            {i === 0 && <span style={{ position: "absolute", bottom: "4px", left: "4px", backgroundColor: "#0d9488", color: "white", fontSize: "10px", fontWeight: "700", padding: "2px 6px", borderRadius: "4px" }}>Principale</span>}
                            <button type="button" onClick={() => removeImage(i)} style={{ position: "absolute", top: "4px", right: "4px", backgroundColor: "rgba(220,38,38,0.9)", color: "white", border: "none", borderRadius: "50%", width: "22px", height: "22px", cursor: "pointer", fontSize: "12px" }}>✕</button>
                          </div>
                        ))}
                        {imagePreviews.length < maxPhotos && (
                          <div onClick={() => imageInputRef.current?.click()} style={{ height: "90px", border: "2px dashed #d1d5db", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#9ca3af", fontSize: "24px" }}>+</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ ...labelStyle, marginBottom: "12px" }}>🎥 Vidéo <span style={{ fontWeight: "400", color: "#9ca3af" }}>(optionnel)</span></label>
                    {!videoPreview ? (
                      <div onClick={() => videoInputRef.current?.click()} style={{ border: "2px dashed #d1d5db", borderRadius: "12px", padding: "24px", textAlign: "center", cursor: "pointer", backgroundColor: "#f9fafb" }}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" style={{ margin: "0 auto 10px", display: "block" }}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                        <p style={{ fontSize: "14px", fontWeight: "600", color: "#6b7280", marginBottom: "4px" }}>Ajouter une vidéo</p>
                        <p style={{ fontSize: "12px", color: "#9ca3af" }}>MP4, MOV, AVI — Max 50 Mo</p>
                      </div>
                    ) : (
                      <div style={{ position: "relative", borderRadius: "12px", overflow: "hidden", border: "2px solid #0d9488" }}>
                        <video src={videoPreview} controls style={{ width: "100%", maxHeight: "220px", display: "block", backgroundColor: "#000" }} />
                        <button type="button" onClick={removeVideo} style={{ position: "absolute", top: "10px", right: "10px", backgroundColor: "rgba(220,38,38,0.9)", color: "white", border: "none", borderRadius: "8px", padding: "6px 12px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>✕ Supprimer</button>
                        <div style={{ padding: "8px 12px", backgroundColor: "#f0fdfa", fontSize: "12px", color: "#0d9488", fontWeight: "600" }}>✓ {videoFile?.name}</div>
                      </div>
                    )}
                    <input ref={videoInputRef} type="file" accept="video/mp4,video/mov,video/avi,video/quicktime" onChange={handleVideoSelect} style={{ display: "none" }} />
                  </div>
                </div>
              )}

              {/* ── STEP 3 ── */}
              {step === 3 && (
                <div>
                  <h2 style={{ fontSize: "17px", fontWeight: "700", color: "#1f2937", marginBottom: "24px", paddingBottom: "14px", borderBottom: "1px solid #f3f4f6" }}>Description & Documents</h2>

                  <div style={{ marginBottom: "20px" }}>
                    <label style={labelStyle}>Description courte *</label>
                    <input type="text" name="description" value={formData.description} onChange={handleChange} required placeholder="Résumé en une ligne" style={inputStyle} />
                  </div>
                  <div style={{ marginBottom: "20px" }}>
                    <label style={labelStyle}>Description détaillée</label>
                    <textarea name="descriptionDetaillee" value={formData.descriptionDetaillee} onChange={handleChange} rows="5" placeholder="Description complète..." style={{ ...inputStyle, resize: "vertical" }} />
                  </div>
                  <div style={{ marginBottom: "24px" }}>
                    <label style={labelStyle}>Caractéristiques (une par ligne)</label>
                    <textarea name="caracteristiques" value={formData.caracteristiques} onChange={handleChange} rows="4" placeholder={"Construction PRVT\nPropulsion 480CV\nCapacité 8 personnes"} style={{ ...inputStyle, resize: "vertical" }} />
                  </div>

                  {/* Fiche technique */}
                  <div style={{ paddingTop: "20px", borderTop: "1px solid #f3f4f6", marginBottom: "30px" }}>
                    <h3 style={{ fontSize: "15px", fontWeight: "700", color: "#1f2937", marginBottom: "6px" }}>Fiche technique</h3>
                    <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "14px" }}>Longueur, Puissance, Matériau, Capacité...</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "12px" }}>
                      {ficheTechnique.map((row, i) => (
                        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "10px", alignItems: "center" }}>
                          <input type="text" placeholder="Clé (ex: Longueur)" value={row.key} onChange={e => updateFicheRow(i, "key", e.target.value)} style={inputStyle} />
                          <input type="text" placeholder="Valeur (ex: 18m00)" value={row.value} onChange={e => updateFicheRow(i, "value", e.target.value)} style={inputStyle} />
                          <button type="button" onClick={() => removeFicheRow(i)} style={{ width: "36px", height: "36px", border: "1px solid #fecaca", backgroundColor: "#fef2f2", color: "#dc2626", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={addFicheRow} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "9px 14px", backgroundColor: "#f0fdfa", color: "#0d9488", border: "1px dashed #0d9488", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Ajouter une ligne
                    </button>
                  </div>

                  {/* Documents */}
                  <div style={{ paddingTop: "24px", borderTop: "1px solid #f3f4f6" }}>
                    <h3 style={{ fontSize: "15px", fontWeight: "700", color: "#1f2937", marginBottom: "4px" }}>📚 Catalogues & Documents</h3>
                    <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>Uploadez vos catalogues, plans techniques, fiches de spécifications ou certificats PDF.</p>
                    <div onClick={() => docInputRef.current?.click()}
                      style={{ border: "2px dashed #6366f1", borderRadius: "12px", padding: "28px", textAlign: "center", cursor: "pointer", backgroundColor: "#f8f9ff", marginBottom: "16px" }}
                      onDragOver={e => { e.preventDefault(); e.currentTarget.style.backgroundColor = "#e0e7ff"; }}
                      onDragLeave={e => { e.currentTarget.style.backgroundColor = "#f8f9ff"; }}
                      onDrop={e => { e.preventDefault(); e.currentTarget.style.backgroundColor = "#f8f9ff"; const files = Array.from(e.dataTransfer.files).filter(f => f.type === "application/pdf"); if (files.length) { const dt = new DataTransfer(); files.forEach(f => dt.items.add(f)); docInputRef.current.files = dt.files; handleDocsSelect({ target: docInputRef.current }); } }}>
                      <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.5" style={{ margin: "0 auto 12px", display: "block" }}>
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                        <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                      </svg>
                      <p style={{ fontSize: "15px", fontWeight: "700", color: "#6366f1", marginBottom: "6px" }}>Cliquez ou glissez vos fichiers PDF</p>
                      <p style={{ fontSize: "12px", color: "#6b7280" }}>Max 10 fichiers — 50 Mo chacun</p>
                    </div>
                    <input ref={docInputRef} type="file" accept="application/pdf" multiple onChange={handleDocsSelect} style={{ display: "none" }} />
                    {docFiles.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {docFiles.map((doc, i) => (
                          <div key={i} style={{ backgroundColor: "#f8f9ff", border: "1px solid #e0e7ff", borderRadius: "10px", padding: "14px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                            <div style={{ width: "40px", height: "40px", borderRadius: "8px", backgroundColor: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "18px" }}>
                              {doc.type_doc === "catalogue" ? "📚" : doc.type_doc === "plan" ? "📐" : doc.type_doc === "fiche" ? "📋" : doc.type_doc === "certificat" ? "🏅" : "📄"}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <input type="text" value={doc.nom_custom} onChange={e => updateDocNom(i, e.target.value)}
                                style={{ width: "100%", padding: "6px 10px", border: "1px solid #e0e7ff", borderRadius: "6px", fontSize: "13px", fontWeight: "600", color: "#1f2937", marginBottom: "6px", outline: "none", boxSizing: "border-box", backgroundColor: "white" }}
                                placeholder="Nom du document" />
                              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                <select value={doc.type_doc} onChange={e => updateDocType(i, e.target.value)}
                                  style={{ padding: "4px 8px", border: "1px solid #e0e7ff", borderRadius: "6px", fontSize: "12px", color: "#6366f1", fontWeight: "600", outline: "none", backgroundColor: "white", cursor: "pointer" }}>
                                  {DOC_TYPES.map(dt => <option key={dt.id} value={dt.id}>{dt.label}</option>)}
                                </select>
                                <span style={{ fontSize: "11px", color: "#9ca3af" }}>{(doc.file.size / 1024).toFixed(0)} Ko</span>
                              </div>
                            </div>
                            <button type="button" onClick={() => removeDoc(i)} style={{ backgroundColor: "#fef2f2", color: "#dc2626", border: "none", borderRadius: "6px", padding: "6px 10px", cursor: "pointer", fontSize: "12px", flexShrink: 0 }}>✕</button>
                          </div>
                        ))}
                        <p style={{ fontSize: "12px", color: "#6b7280" }}>{docFiles.length}/10 fichier{docFiles.length > 1 ? "s" : ""}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
              {step > 1 ? (
                <button type="button" onClick={() => setStep(step - 1)} style={{ padding: "12px 24px", backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer", color: "#374151" }}>← Précédent</button>
              ) : <div />}
              {step < 3 ? (
                <button type="button" onClick={() => setStep(step + 1)} style={{ padding: "12px 24px", backgroundColor: "#0d9488", color: "white", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>Suivant →</button>
              ) : (
                <button type="submit" disabled={uploading} style={{ padding: "12px 28px", backgroundColor: uploading ? "#9ca3af" : "#f59e0b", color: "white", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: uploading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                  {uploading ? (
                    <><div style={{ width: "16px", height: "16px", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />Upload en cours...</>
                  ) : (
                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                    {editMode ? "Enregistrer les modifications" : "Envoyer à l'administrateur"}</>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Footer />
    </div>
  );
}

export default AjouterAnnonce;